/**
 * Flashcard Scheduler
 *
 * Determines which cards are due, applies ordering/filtering for
 * different study modes, and manages the review queue.
 *
 * Daily Deck system: 100 active cards per day, rotating in new cards
 * as the user masters (2 correct rounds) the current ones.
 */

import { isDueToday, daysOverdue } from '../../../shared/utils/dateUtils';

/** Maximum cards in the active daily deck */
export const DAILY_DECK_SIZE = 100;

/** Correct rounds needed before a card graduates from the daily deck */
export const GRADUATION_THRESHOLD = 2;

/**
 * Get all cards due for review today (next_review_date <= today).
 * Sorted by most overdue first.
 */
export function getDueCards(cards) {
  return cards
    .filter(card => isDueToday(card.sr_data.next_review_date))
    .sort((a, b) => {
      const aOverdue = daysOverdue(a.sr_data.next_review_date);
      const bOverdue = daysOverdue(b.sr_data.next_review_date);
      return bOverdue - aOverdue; // Most overdue first
    });
}

/**
 * Build the daily deck: up to DAILY_DECK_SIZE cards, prioritized as:
 *   1. Overdue cards (most overdue first)
 *   2. New/unseen cards (never reviewed)
 *   3. Cards due today
 *
 * Returns { active, waiting } where active is the initial batch and
 * waiting is the overflow queue that feeds in as cards graduate.
 */
export function buildDailyDeck(cards) {
  const eligible = getDueCards(cards);

  // Partition into overdue, new (never seen), and due-today
  const overdue = [];
  const unseen = [];
  const dueToday = [];

  for (const card of eligible) {
    const over = daysOverdue(card.sr_data.next_review_date);
    if (card.stats.times_seen === 0) {
      unseen.push(card);
    } else if (over > 0) {
      overdue.push(card);
    } else {
      dueToday.push(card);
    }
  }

  // Combine in priority order
  const prioritized = [...overdue, ...shuffleArray(unseen), ...dueToday];
  const active = prioritized.slice(0, DAILY_DECK_SIZE).map(c => c.id);
  const waiting = prioritized.slice(DAILY_DECK_SIZE).map(c => c.id);

  return { active, waiting };
}

/**
 * Get all cards in a specific domain for Cram mode.
 * Shuffled randomly, no SR scheduling impact.
 */
export function getCramCards(cards, domainId) {
  const domainCards = domainId
    ? cards.filter(card => card.domain === domainId)
    : [...cards];
  return shuffleArray(domainCards);
}

/**
 * Get cards with accuracy below threshold for Weak Spots mode.
 * Sorted worst accuracy first.
 */
export function getWeakCards(cards, threshold = 0.5) {
  return cards
    .filter(card => {
      if (card.stats.times_seen === 0) return false;
      const accuracy = card.stats.times_correct / card.stats.times_seen;
      return accuracy < threshold;
    })
    .sort((a, b) => {
      const aAcc = a.stats.times_correct / a.stats.times_seen;
      const bAcc = b.stats.times_correct / b.stats.times_seen;
      return aAcc - bAcc; // Worst first
    });
}

/**
 * Get new cards that haven't been seen yet.
 */
export function getNewCards(cards) {
  return cards.filter(card => card.stats.times_seen === 0);
}

/**
 * Count cards due by domain.
 */
export function getDueCountByDomain(cards) {
  const counts = {};
  const dueCards = getDueCards(cards);
  for (const card of dueCards) {
    counts[card.domain] = (counts[card.domain] || 0) + 1;
  }
  return counts;
}

/**
 * Fisher-Yates shuffle.
 */
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
